import React, { useContext, useEffect, useState } from 'react';
import { ScrapbookFile } from '@/api/scrapbook-data';
import ContentPreviewWraper from './ContentPreviewWraper';
import {
  AppInsightsContext,
  Dropdown,
  MetadataContext,
  TrackEventKey,
  TrackEventName,
  TrackEventType,
  useNotification,
  UserContext,
} from '@/components';
import { useRouter } from 'next/router';
import { colorFromBgColor, fixColor, hexToRgb, isColorDark } from '@/utils';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import { Palette } from '@/types/ColorLibraryModel';
import { loadUserPalettes, savePalette } from '@/api/color-library';

interface ColourPreviewProps {
  content: ScrapbookFile | null;
  onClose: () => void;
}
const ColourPreview = ({ content, onClose }: ColourPreviewProps) => {
  const [palettes, setPalettes] = useState<Palette[]>([]);
  const [chosenPaletteId, setChosenPaletteId] = useState<string | null>(null);

  const user = useContext(UserContext);
  const appInsights = useContext(AppInsightsContext);
  const { pageId } = useContext(MetadataContext);
  const router = useRouter();
  const t = useTranslations();
  const { showNotification } = useNotification();

  useEffect(() => {
    loadUserPalettes().then(({ result }) => setPalettes(result));
  }, []);

  if (!content) return null;

  const handleDownload = () => {
    appInsights.trackCustomEvent(TrackEventName.REPORT, TrackEventKey.COLOUR_CHIP, TrackEventType.DOWNLOAD, user, {
      pageId,
      pagePath: router.asPath,
    });

    try {
      const form = document.getElementById('stDownloadPaletteForm') as HTMLFormElement;

      console.log(form);
      if (form) {
        const nameInput = form.querySelector('input[name="name"]') as HTMLInputElement;
        const colourIdsInput = form.querySelector('input[name="colourIds"]') as HTMLInputElement;

        if (nameInput && colourIdsInput) {
          nameInput.value = content.content.displayName;
          colourIdsInput.value = content.content.id;
          form.submit();
        }
      }
    } catch (error) {
      console.error('Error downloading color:', error);
    }
  };

  const onAddToPaletteClick = async () => {
    if (!chosenPaletteId) return;

    const palette = palettes.find((p) => String(p.id) === chosenPaletteId);
    if (!palette) return;

    if (palette.colours.find((c) => c.id === content.content.id)) return;
    palette.colours.push(content.content);

    savePalette(palette).then(({ success }) => {
      if (success) {
        showNotification('Color was added to palette successfully', 'success');
      }
    });
  };

  const isDark = isColorDark(content.content.hexCode);

  return (
    <ContentPreviewWraper
      content={content}
      onClose={onClose}
      title="Colour"
      subTitle="Stylus: Innovation Research & Advisory"
      onDownload={handleDownload}
    >
      <div className="flex h-full w-full flex-col items-center justify-center">
        <div
          className={classNames(
            'mx-10 w-[400px] bg-cover bg-center bg-no-repeat p-8',
            isDark ? 'module-base-light' : 'module-base-dark',
            content.content.effect === 'Metallic' && 'bg-metallic',
            content.content.effect === 'Lustre' && 'bg-lustre',
            content.content.effect === 'Chrome' && 'bg-chrome',
          )}
          style={{
            backgroundColor: fixColor(content.content.hexCode),
          }}
        >
          <h3 className="color-from-bg text-base font-medium sm:mb-4 sm:text-lg md:mb-4 md:text-lg lg:mb-6 lg:text-3xl">
            {content.content.displayName}
          </h3>

          <div className="color-from-bg mb-6 grid grid-cols-3 gap-6 sm:pt-6 md:pt-6 lg:gap-4 lg:pt-7">
            <div>
              <div className="mb-1 text-xs font-semibold lg:text-sm">Pantone</div>
              <div className="mb-4 text-base">{content.content.tpxCode || '-'}</div>
            </div>
            <div>
              <div className="mb-1 text-xs font-semibold lg:text-sm">Hex</div>
              <div className="mb-4 text-base">#{content.content.hexCode}</div>
            </div>
            <div>
              <div className="mb-1 text-xs font-semibold lg:text-sm">RAL</div>
              <div className="mb-4 text-base">{content.content.ralCode || '-'}</div>
            </div>
            <div>
              <div className="mb-1 text-xs font-semibold lg:text-sm">Coated</div>
              <div className="mb-4 text-base">{content.content.coatedCode || '-'}</div>
            </div>
            <div>
              <div className="mb-1 text-xs font-semibold lg:text-sm">RGB</div>
              <div className="mb-4 text-base">{hexToRgb(content.content.hexCode)?.join('/')}</div>
            </div>
            <div>
              <div className="mb-1 text-xs font-semibold lg:text-sm">NCS</div>
              <div className="mb-4 text-base">{content.content.ncsCode || '-'}</div>
            </div>
          </div>

          {user.userId && (
            <div className="border-from-bg/10 flex min-h-[34px] items-center rounded-full border">
              <Dropdown
                placeholder={t('colourLibrary.$palette')}
                name="palette"
                options={[...palettes.map((p) => ({ label: p.name, value: String(p.id) }))]}
                onChange={(e) => setChosenPaletteId(e.target.value || '')}
                className={classNames(
                  'w-1/2 px-6 py-2',
                  '[&_.main-button]:h-auto [&_.main-button]:w-full [&_.main-button]:cursor-pointer [&_.main-button]:border-none [&_.main-button]:bg-transparent [&_.main-button]:p-0 [&_.main-button]:text-center [&_.main-button]:outline-none',
                  '[&_.arrow_svg]:duration-0',
                  isDark
                    ? '[&_.arrow_svg]:fill-white [&_.popover-title]:text-white [&_.popover-title.placeholder]:text-white/70'
                    : '[&_.popover-title]:text-black [&_.popover-title.placeholder]:text-black/60',
                )}
                contentWrapperClassName="[&_.content]:bg-white [&_.content]:text-black w-full [&_.content]:w-full [&_.option]:text-lg"
                offset={8}
              />
              <div className="border-from-bg/10 h-[18px] border-r"></div>
              <button
                onClick={onAddToPaletteClick}
                disabled={!chosenPaletteId}
                className={classNames(
                  chosenPaletteId && 'cursor-pointer',
                  'color-from-bg w-1/2 px-5 font-semibold whitespace-nowrap transition-all duration-0 hover:opacity-80 disabled:opacity-50',
                )}
              >
                {t('colourLibrary.$addToPalette')}
              </button>
            </div>
          )}

          <div className="mt-3 flex items-center justify-center gap-4">
            <a
              href="https://app.stylus.com/tools/colour-library"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block rounded-2xl border px-4 py-2 font-semibold transition-opacity hover:opacity-80"
              style={{
                color: colorFromBgColor(content.content.hexCode) || (isDark ? 'white' : 'black'),
                borderColor:
                  colorFromBgColor(content.content.hexCode, 0.2) ||
                  (isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'),
              }}
            >
              Create a palette in Colour Library
            </a>
          </div>
        </div>
      </div>
      {/* Hidden form for downloads */}
      <form
        id="stDownloadPaletteForm"
        method="POST"
        target="_blank"
        action={`${process.env.NEXT_PUBLIC_API_URL || 'https://uat-web-api.testing-stylus.com'}/umbraco/Api/ColourPaletteRenderer/RenderPalette2`}
        className="hidden"
      >
        <input type="hidden" name="name" />
        <input type="hidden" name="colourIds" />
      </form>
    </ContentPreviewWraper>
  );
};

export default ColourPreview;
